import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_URL;

// --- Types ---

// FIX: Changed from 'interface' to 'type' to fix index signature parsing
export type TokenSources = {
  subscription: number;
  purchased: number;
  [key: string]: number; // This allows dynamic keys like 'gameplay_tokens'
};

export type WalletRegistry = {
  [tokenType: string]: TokenSources;
};

export type OrgDetails = {
  id: string;
  name: string;
  wallets: WalletRegistry;
  limits: Record<string, number>;
  features: string[];
  activeSubscriptions?: Array<{
    id: string;
    planId: string;
    status: string;
    currentPeriodStart?: string;
    expiryDate?: string;
    gracePeriodEnd?: string | null;
    cancelAtPeriodEnd?: boolean;
    updatedAt?: string;
  }>;
  activePlanIds?: string[];
  currentSubscription?: {
    id: string;
    planId: string;
    status: string;
  } | null;
  currentPlanId?: string | null;
  currentPlanName?: string | null;
  subscriptionPlanId?: string | null;
  subscriptionPriceId?: string | null;
  planId?: string | null;
  priceId?: string | null;
};

export type TokenGrant = {
  token_type: string;
  amount: number;
  bucket: 'subscription' | 'purchased';
};

export type BillingPlan = {
  id: string;
  productId: string;
  name: string;
  active: boolean;
  currency: string;
  amount: number;
  interval: 'day' | 'week' | 'month' | 'year' | 'one_time';
  features: string[];
  limits: Record<string, number>;
  /**
   * Monthly token allowances granted by this plan/price (sessions, vouchers,
   * generation tokens, whatsapp credits, etc). Populated by the backend from
   * Stripe price metadata. Empty/undefined on older cached plans until the
   * backend redeploys and webhooks refresh.
   */
  tokenGrants?: TokenGrant[];
  updatedAt: string;
};

export type OrgUsageSnapshot = {
  campaigns: {
    active: number;
    inactive: number;
    ended: number;
    total: number;
    limit: number | null;
    counterStored: number;
    counterDrift: boolean;
  };
  tenants: { used: number; limit: number | null };
  teamMembers: { used: number; limit: number | null };
  registrations: {
    used: number | null;
    limit: number | null;
    hardCap: number | null;
    note?: string;
  };
  generatedAt: string;
};

// --- Helper ---
async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    // Prefer ID Token for custom claims, fallback to Access Token
    const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString();

    if (!token) throw new Error("No token found");

    return { Authorization: `Bearer ${token}` };
  } catch (error) {
    console.error("Error fetching auth session", error);
    throw new Error("You must be logged in to do this.");
  }
}

// --- Service ---
export const billingService = {

  async startCheckout(
    priceId: string,
    options?: { successUrl?: string; cancelUrl?: string }
  ) {
    const successUrl = options?.successUrl ?? `${window.location.origin}/billing/success`;
    const cancelUrl = options?.cancelUrl ?? `${window.location.origin}/billing`;
    const headers = await getAuthHeaders();

    try {
      const response = await axios.post(
        `${API_URL}/billing/checkout`,
        { priceId, successUrl, cancelUrl },
        { headers }
      );
      return response.data.url;
    } catch (error) {
      console.error("Billing Service Error:", error);
      throw error;
    }
  },

  async getOrgDetails(): Promise<OrgDetails> {
    const headers = await getAuthHeaders();
    const response = await axios.get<OrgDetails>(
      `${API_URL}/org/me`,
      { headers }
    );
    const data = response.data as OrgDetails;

    return {
      id: data.id,
      name: data.name,
      wallets: data.wallets || {},
      limits: data.limits || {},
      features: data.features || [],
      activeSubscriptions: data.activeSubscriptions || [],
      activePlanIds: data.activePlanIds || [],
      currentSubscription: data.currentSubscription ?? null,
      currentPlanId: data.currentPlanId ?? null,
      currentPlanName: data.currentPlanName ?? null,
      subscriptionPlanId: data.subscriptionPlanId ?? null,
      subscriptionPriceId: data.subscriptionPriceId ?? null,
      planId: data.planId ?? null,
      priceId: data.priceId ?? null,
    };
  },

  async getPlans(): Promise<BillingPlan[]> {
    const response = await axios.get<BillingPlan[]>(`${API_URL}/plans`);
    return response.data;
  },

  async getOrgUsage(): Promise<OrgUsageSnapshot> {
    const headers = await getAuthHeaders();
    const response = await axios.get<{ success: boolean; data: OrgUsageSnapshot }>(
      `${API_URL}/manager/org/usage`,
      { headers }
    );
    return response.data.data;
  }
};
